import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { ShiftTypesRadioInput } from '@jobs/components';
import { Colors } from 'works/legacy/lib/style-helper';
import { getRateByEmploymentType } from '@institutions/services';
import { ApprovalChainConfig } from '@approvals/components/approval-chain-template-select';
import { getLengthInWeeksInclusiveValue } from 'works/legacy/services/length-in-weeks-service';
import { BillingCardDeviationAlert } from 'works/components/billing-card-deviation-alert';
import { Store } from 'antd/lib/form/interface';
import { AssignmentType } from 'works/components/extension-offer/create-extension-offer-flow';
import { useQuery } from '@common/hooks';
import { EffectiveBillingCardQuery } from 'works/graphql/effective-billing-card';
import { toUSFormat, toYearMonthDay } from 'works/legacy/lib/date-helper';
import { Currency, CurrencyInput, ErrorHandler } from '@common/components';
import {
  Alert,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Row,
  Spin,
  UploadFile,
} from 'antd';
import { OvertimeThresholdSelect } from '@timekeeping/components';
import { ShiftType } from '@common/graphql';
import { BillingCardType } from '@institutions/graphql';

export type ExtendOfferFormProps = {
  assignment: AssignmentType;
  onFinish: (values: ExtensionOfferValues) => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  form: FormInstance;
};

export type FormInitialValues = {
  startDate: moment.Moment;
  billingRegularRate: number;
  shiftType: string;
  shiftsPerWeek: number;
  hoursPerShift: number;
  overtimeThreshold: number;
};

export type FormValues = {
  approvalDocument: {
    file: UploadFile;
  };
  startDate: moment.Moment;
  endDate: moment.Moment;
  extensionLength: number;
  timeoffDescription: string;
  shiftType: ShiftType;
  shiftsPerWeek: number;
  hoursPerShift: number;
  deviated: boolean;
  billingRegularRate: number;
  approvalChainTemplateSource: string;
  commentContent: string;
  overtimeThreshold: number;
};

export type ExtensionOfferValues = FormValues & {
  deviated: boolean;
  billingCard?: BillingCardType;
};

export function ExtensionOfferForm({
  assignment,
  onFinish,
  setLoading,
  form,
}: ExtendOfferFormProps) {
  const minimalStartDate = moment(assignment.finalEndDate).add(1, 'day');

  const [currentStartDate, setCurrentStartDate] = useState<moment.Moment>(
    form.getFieldValue('startDate') || minimalStartDate
  );
  const [currentRate, setCurrentRate] = useState<number>(
    assignment.billingRegularRate
  );
  const [isDeviated, setIsDeviated] = useState<boolean>(false);
  const approvalChainActionType = 'extension_offer_active';

  const initialValues: FormInitialValues = {
    startDate: minimalStartDate,
    billingRegularRate: assignment.billingRegularRate,
    shiftsPerWeek: assignment.shiftsPerWeek,
    hoursPerShift: assignment.hoursPerShift,
    shiftType: assignment.shiftType,
    overtimeThreshold: assignment.lastAssignmentTerm.overtimeThreshold,
  };

  const {
    data: billingCardData,
    error: billingCardError,
    loading: loadingBillingCard,
  } = useQuery(EffectiveBillingCardQuery, {
    variables: {
      effectiveDate: toYearMonthDay(currentStartDate),
      organizationUnitId: assignment.lastAssignmentTerm.organizationUnit.id,
      roleId: assignment.staffingRequest.role.id,
      roleLevel: assignment.staffingRequest.roleLevel,
    },
    onCompleted: (data) => {
      if (!data?.effectiveBillingCard) {
        setCurrentRate(assignment.billingRegularRate);
        setIsDeviated(true);
        return;
      }

      const billingCardRate = getRateByEmploymentType(
        data.effectiveBillingCard,
        form.getFieldValue('shiftType'),
        assignment.employmentType
      );
      if (!isDeviated) {
        form.setFieldsValue({
          billingRegularRate: billingCardRate,
        });
      }
      setCurrentRate(billingCardRate || 0);
      setIsDeviated(
        billingCardRate !== form.getFieldValue('billingRegularRate')
      );
    },
  });

  useEffect(() => {
    setLoading(loadingBillingCard);
  }, [loadingBillingCard, setLoading]);

  if (billingCardError) {
    return <ErrorHandler error={billingCardError} log="ExtensionOfferForm" />;
  }

  const billingCard = billingCardData?.effectiveBillingCard;
  const hasBillingCard = !!billingCard;
  const currentRateSourceDescription = hasBillingCard
    ? 'Rate card rate: '
    : 'Current rate: ';

  const onStartDateChange = (value: moment.Moment) => {
    form.resetFields(['endDate', 'extensionLength']);
    setCurrentStartDate(value);
  };

  const onLengthChange = (event: { target: { value: string } }) => {
    const weeks = parseInt(event.target.value, 10);
    if (weeks) {
      // Without this DatePicker won't show updated values
      form.resetFields(['endDate']);

      form.setFieldsValue({
        endDate: currentStartDate.clone().add(-1, 'day').add(weeks, 'weeks'),
      });
    }
  };

  const onEndDateChange = (value: moment.Moment) => {
    form.setFieldsValue({
      extensionLength: getLengthInWeeksInclusiveValue(currentStartDate, value),
    });
  };

  const handleSubmit = (values: Store) => {
    onFinish({
      ...(values as FormValues),
      deviated: isDeviated,
      billingCard: billingCardData?.effectiveBillingCard,
    });
  };

  function onShiftTypeChange(shiftType: ShiftType) {
    if (!hasBillingCard) return;
    const newBillingCardRate = getRateByEmploymentType(
      billingCard,
      shiftType,
      assignment.employmentType
    );
    if (!isDeviated) {
      form.setFieldsValue({ billingRegularRate: newBillingCardRate });
    }
    setCurrentRate(newBillingCardRate || 0);
    setIsDeviated(
      newBillingCardRate !== form.getFieldValue('billingRegularRate')
    );
  }

  function onBillingRegularRateChange(billingRegularRate: number) {
    if (!hasBillingCard) return;
    setIsDeviated(billingRegularRate !== currentRate);
  }

  function handleOnValuesChanged({
    endDate,
    startDate,
    shiftType,
    billingRegularRate,
  }: Store) {
    if (billingRegularRate) onBillingRegularRateChange(billingRegularRate);
    if (shiftType) onShiftTypeChange(shiftType);
    if (endDate) onEndDateChange(endDate);
    if (startDate) onStartDateChange(startDate);
  }

  return (
    <div className="ExtendOfferForm">
      <Alert
        message={
          <>
            All changes will go into effect on{' '}
            <strong>{toUSFormat(minimalStartDate)}</strong>, the day after the
            current end date.
          </>
        }
        type="warning"
        showIcon
      />
      <Form
        onFinish={handleSubmit}
        initialValues={initialValues}
        form={form}
        layout="vertical"
        onValuesChange={handleOnValuesChanged}
      >
        <Row gutter={[8, 8]}>
          <Col span={8}>
            <Form.Item
              label="Extension Start Date"
              name="startDate"
              rules={[
                { required: true, message: 'Please enter an start date' },
              ]}
            >
              <DatePicker
                autoComplete="off"
                name="startDate"
                size="large"
                placeholder="mm/dd/yyyy"
                format="MM/DD/YYYY"
                showToday={false}
                defaultPickerValue={moment(assignment.finalEndDate)}
                disabledDate={(current) => {
                  return (
                    current &&
                    current < moment(assignment.finalEndDate).endOf('day')
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Extension End Date"
              name="endDate"
              rules={[{ required: true, message: 'Please enter an end date' }]}
            >
              <DatePicker
                data-testid="ext-end-date"
                autoComplete="off"
                name="endDate"
                size="large"
                placeholder="mm/dd/yyyy"
                format="MM/DD/YYYY"
                showToday={false}
                defaultPickerValue={currentStartDate}
                disabledDate={(current) => {
                  return current && current < currentStartDate.startOf('day');
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Length (in weeks)"
              name="extensionLength"
              rules={[
                {
                  required: true,
                  message: 'Please enter an ext. length',
                },
              ]}
            >
              <InputNumber
                data-testid="extension-length-input"
                size="large"
                min={1}
                onBlur={onLengthChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Time Off Request" required>
          <Form.Item
            name="timeoffDescription"
            rules={[{ required: true, message: 'Please enter a time off' }]}
            noStyle
          >
            <Input data-testid="time-off-input" size="large" />
          </Form.Item>
          <div className="hint">
            Current time off: <strong>{assignment.timeoffDescription}</strong>
          </div>
        </Form.Item>

        <ShiftTypesRadioInput />

        <Spin spinning={loadingBillingCard}>
          <Form.Item label="Rate" required>
            <Form.Item
              name="billingRegularRate"
              rules={[
                { required: true, message: 'Please enter a rate' },
                () => ({
                  validator(test, value) {
                    if (value > 0) return Promise.resolve();
                    return Promise.reject(
                      new Error('Rate must be at least $1')
                    );
                  },
                }),
              ]}
              noStyle
            >
              <CurrencyInput size="large" />
            </Form.Item>
            <div className="hint">
              {currentRateSourceDescription}
              <strong>
                <Currency value={currentRate} />
              </strong>
            </div>
          </Form.Item>
          {hasBillingCard && isDeviated && (
            <Row gutter={[8, 16]}>
              <BillingCardDeviationAlert type="extensionOffer" />
            </Row>
          )}
        </Spin>

        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Form.Item label="Shifts Per Week" required>
              <Form.Item
                name="shiftsPerWeek"
                rules={[
                  {
                    required: true,
                    message: 'Please enter the amount of shifts per week',
                  },
                  () => ({
                    validator(test, value) {
                      if (value > 0) return Promise.resolve();
                      return Promise.reject(
                        new Error('Must be greater than 0')
                      );
                    },
                  }),
                ]}
                noStyle
              >
                <InputNumber size="large" />
              </Form.Item>
              <div className="hint">
                Current shifts per week:{' '}
                <strong>{assignment.shiftsPerWeek}</strong>
              </div>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Hours Per Shift" required>
              <Form.Item
                name="hoursPerShift"
                rules={[
                  {
                    required: true,
                    message: 'Please enter the amount of hours per shift',
                  },
                  () => ({
                    validator(test, value) {
                      if (value > 0) return Promise.resolve();
                      return Promise.reject(
                        new Error('Must be greater than 0')
                      );
                    },
                  }),
                ]}
                noStyle
              >
                <InputNumber size="large" />
              </Form.Item>
              <div className="hint">
                Current hours per week:{' '}
                <strong>{assignment.hoursPerShift}</strong>
              </div>
            </Form.Item>
          </Col>
        </Row>

        {!!assignment.lastAssignmentTerm.overtimeThreshold && (
          <OvertimeThresholdSelect size="large" />
        )}

        <ApprovalChainConfig
          organizationUnitId={assignment.lastAssignmentTerm.organizationUnit.id}
          actionType={approvalChainActionType}
          form={form}
        />
      </Form>
      <style jsx>
        {`
          .ExtendOfferForm :global(.ant-input-number),
          .ExtendOfferForm :global(.ant-form-item-control-input-content),
          .ExtendOfferForm :global(.ant-picker) {
            width: 100%;
          }

          .ExtendOfferForm hr {
            border: none;
            border-top: 1px solid ${Colors.steel};
            margin-bottom: 24px;
          }

          .ExtendOfferForm .hint {
            margin-top: 8px;
            color: ${Colors.midnightLighter};
          }

          .ExtendOfferForm :global(.ant-alert) {
            margin-bottom: 1.5rem;
            margin-top: 0.5rem;
          }
        `}
      </style>
    </div>
  );
}
